import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { provincesApi } from '../../services/settingsApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function ProvincesTab() {
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProvinces, setFilteredProvinces] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProvince, setEditingProvince] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name_en: '',
    name_lao: '',
    name_th: '',
    country: 'Laos',
    status: 'active'
  });
  const { getMessage, isLao, isThai, getCommonLabel } = useTranslation();

  const fetchProvinces = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await provincesApi.getAll();
      setProvinces(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching provinces:', error);
      toast.error('Failed to load provinces');
      setProvinces([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  // Filter provinces based on search query
  useEffect(() => {
    let filtered = provinces;

    if (searchQuery.trim() !== '') {
      filtered = provinces.filter(province =>
        province.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        province.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        province.name_lao?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        province.name_th?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        province.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProvinces(filtered);
  }, [searchQuery, provinces]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddProvince = () => {
    setEditingProvince(null);
    setFormData({
      code: '',
      name_en: '',
      name_lao: '',
      name_th: '',
      country: 'Laos',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditProvince = (province) => {
    setEditingProvince(province);
    setFormData({
      code: province.code || '',
      name_en: province.name_en || '',
      name_lao: province.name_lao || '',
      name_th: province.name_th || '',
      country: province.country || 'Laos',
      status: province.status || 'active'
    });
    setShowModal(true);
  };

  const handleDeleteProvince = async (province) => {
    if (!window.confirm('Are you sure you want to delete this province? This will also affect associated districts.')) {
      return;
    }

    try {
      await provincesApi.delete(province.id);
      toast.success('Province deleted successfully');
      fetchProvinces();
    } catch (error) {
      console.error('Error deleting province:', error);
      toast.error('Failed to delete province');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingProvince) {
        await provincesApi.update(editingProvince.id, formData);
        toast.success('Province updated successfully');
      } else {
        await provincesApi.create(formData);
        toast.success('Province created successfully');
      }

      setShowModal(false);
      fetchProvinces();
    } catch (error) {
      console.error('Error saving province:', error);
      toast.error('Failed to save province');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProvince(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            🗺️ {getMessage('settings.provinces') || 'Provinces'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {getMessage('settings.provinces_desc') || 'Manage provinces and states in the system'}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchProvinces}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            🔄 {getMessage('common.refresh') || 'Refresh'}
          </button>
          <div>
            <input
              id="prov-import-input"
              type="file"
              accept=".xls,.xlsx"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  toast.loading('Importing...', { id: 'import-provinces' });
                  const resp = await provincesApi.importExcel(file);
                  toast.success(`Import completed: provinces + districts`, { id: 'import-provinces' });
                  await fetchProvinces();
                } catch (err) {
                  console.error('Import failed', err);
                  toast.error('Import failed');
                } finally {
                  e.target.value = '';
                }
              }}
            />
            <button
              onClick={() => document.getElementById('prov-import-input').click()}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              ⬆️ {getMessage('common.import') || 'Import Excel'}
            </button>
          </div>
          <button
            onClick={handleAddProvince}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            ➕ {getCommonLabel('add') || 'Add Province'}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder={getMessage('common.search') || 'Search provinces...'}
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
            🔍
          </div>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {filteredProvinces.length} province(s) found
        </div>
      </div>

      {/* Provinces Table */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        ) : filteredProvinces.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🗺️</div>
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery ? 'No provinces found matching your search' : 'No provinces available'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name (EN)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name (LAO)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name (TH)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Districts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
              {filteredProvinces.map((province) => (
                <tr key={province.id} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                      {province.code || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {province.name_en || province.name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {province.name_lao || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {province.name_th || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      <button
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        onClick={() => {
                          // You can implement a modal to show districts here
                          const displayName = isLao() ? (province.name_lao || province.name_en || province.name_th) : (isThai() ? (province.name_th || province.name_en || province.name_lao) : (province.name_en || province.name_lao || province.name_th));
                          alert(`Districts for ${displayName}: This feature can be enhanced to show district list`);
                        }}
                      >
                        {province.district_count || '0'} districts
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${province.status === 'active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                      {province.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEditProvince(province)}
                      className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteProvince(province)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingProvince ? 'Edit Province' : 'Add New Province'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Province Code *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., VTE, LPB, PSL"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="e.g., Laos"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name (English) *
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name (Lao) *
                </label>
                <input
                  type="text"
                  name="name_lao"
                  value={formData.name_lao}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name (Thai)
                </label>
                <input
                  type="text"
                  name="name_th"
                  value={formData.name_th}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  {editingProvince ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}